<script setup lang="ts">
import { TransitionGroup } from 'vue';
import { CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { toast, useToasts, type ToastKind } from '@/lib/toast';

/**
 * Mounted once in App.vue. Renders the queue from `useToasts()` as a
 * stacked column in the bottom-right of the viewport. Each toast is
 * dismissable via the X button; auto-dismiss is handled by the store.
 */

const toasts = useToasts();

const kindClasses: Record<ToastKind, string> = {
    success: 'bg-green-50 text-green-900 border-green-200 dark:bg-green-900/30 dark:text-green-200 dark:border-green-800',
    error:   'bg-red-50 text-red-900 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800',
    info:    'bg-slate-50 text-slate-900 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700',
};

const kindIcons: Record<ToastKind, typeof CheckCircleIcon> = {
    success: CheckCircleIcon,
    error:   ExclamationCircleIcon,
    info:    InformationCircleIcon,
};
</script>

<template>
    <!--
      Anchored top-right, just below the AppShell header (h-14 = 56px).
      `top-16` gives a small gap so toasts sit cleanly under the user
      menu. Enter slides down from above; pointer-events disabled on the
      container so the area between toasts stays click-through.
    -->
    <!-- Above the dialogs (z-50), or a toast raised by one renders behind
         its own backdrop and reads as a message meant for another screen. -->
    <div class="pointer-events-none fixed inset-x-0 top-16 z-100 flex flex-col items-end gap-2 px-4 sm:px-6">
        <TransitionGroup
            tag="div"
            class="flex w-full max-w-sm flex-col items-stretch gap-2"
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="-translate-y-2 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0">
            <div
                v-for="t in toasts.items"
                :key="t.id"
                role="status"
                class="pointer-events-auto flex items-start gap-3 rounded-md border px-3 py-2 text-sm shadow-md"
                :class="kindClasses[t.kind]">
                <component :is="kindIcons[t.kind]" class="mt-0.5 h-5 w-5 shrink-0" />
                <p class="flex-1 leading-5">{{ t.message }}</p>
                <button
                    type="button"
                    class="-m-1 rounded p-1 opacity-70 hover:opacity-100"
                    :aria-label="$t('ui.toast.dismiss')"
                    @click="toast.dismiss(t.id)">
                    <XMarkIcon class="h-4 w-4" />
                </button>
            </div>
        </TransitionGroup>
    </div>
</template>
